'use client';
import { useState } from 'react';
import Link from 'next/link';
import { industries } from '@/lib/seo-database';
// Calculation assumptions — ALL from verified sources
const ASSUMPTIONS = {
  aiHandlePercentage: 0.80,       // IBM: chatbots handle up to 80% of routine inquiries
  costReductionPercentage: 0.30,  // IBM: cutting support costs by 30%
  avgResponseTimeReductionHours: 5.93, // Freshworks: 6hrs → 4min = ~5.93hrs saved
  roiPerPound: 2.80,              // Industry avg: $3.50/$2.80 per $1 over 12-18mo (converted)
  monthsToPositiveROI: 11,        // Fullview: 8-14 months average = ~11
  monthsToInitialBenefits: 2.5,   // Fullview: 60-90 days = ~2.5 months
  loyaltyMultiplier: 2.4,         // Forrester: 2.4x more loyal with fast resolution
};
const SOURCES = [
  { claim: 'AI handles up to 80% of routine inquiries', source: 'IBM', url: 'https://www.nexgencloud.com/blog/case-studies/how-ai-and-rag-chatbots-cut-customer-service-costs-by-millions' },
  { claim: 'AI cuts customer support costs by 30%', source: 'IBM', url: 'https://www.nexgencloud.com/blog/case-studies/how-ai-and-rag-chatbots-cut-customer-service-costs-by-millions' },
  { claim: 'AI reduced first response time from 6hrs to 4min', source: 'Freshworks', url: 'https://www.freshworks.com/How-AI-is-unlocking-ROI-in-customer-service/' },
  { claim: 'Average $2.80 return per $1 invested (12-18 months)', source: 'Fullview / Industry analysis', url: 'https://www.fullview.io/blog/ai-customer-service-stats' },
  { claim: 'Initial benefits within 60-90 days', source: 'Fullview / Industry analysis', url: 'https://www.fullview.io/blog/ai-customer-service-stats' },
  { claim: '2.4x more loyal when resolved quickly', source: 'Forrester', url: 'https://www.freshworks.com/How-AI-is-unlocking-ROI-in-customer-service/' },
];
export function ROICalculatorClient() {
  const [industry, setIndustry] = useState('');
  const [enquiriesPerMonth, setEnquiriesPerMonth] = useState(200);
  const [currentResponseHours, setCurrentResponseHours] = useState(2);
  const [staffCount, setStaffCount] = useState(2);
  const [hourlyRate, setHourlyRate] = useState(15);
  const [showResults, setShowResults] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  // Calculations
  const enquiriesHandledByAI = Math.round(enquiriesPerMonth * ASSUMPTIONS.aiHandlePercentage);
  const avgMinutesPerEnquiry = 8; // conservative estimate
  const hoursFreedPerMonth = Math.round((enquiriesHandledByAI * avgMinutesPerEnquiry) / 60);
  const monthlyCostSaving = Math.round(hoursFreedPerMonth * hourlyRate);
  const annualCostSaving = monthlyCostSaving * 12;
  const estimatedMonthlyAICost = 199; // mid-tier estimate in $
  const annualAICost = estimatedMonthlyAICost * 12;
  const annualNetSaving = annualCostSaving - annualAICost;
  const roiPercentage = annualAICost > 0 ? Math.round((annualNetSaving / annualAICost) * 100) : 0;
  const handleCalculate = () => {
    setShowResults(true);
    // Scroll to results
    setTimeout(() => {
      document.getElementById('roi-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  return (
    <div className="max-w-4xl mx-auto px-6 py-20">
      {/* Breadcrumbs */}
      <nav className="text-sm text-gray-400 mb-8" aria-label="Breadcrumb">
        <Link href="/" className="hover:text-white transition-colors">Home</Link>
        <span className="mx-2">/</span>
        <Link href="/tools" className="hover:text-white transition-colors">Tools</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-300">ROI Calculator</span>
      </nav>
      <h1 className="text-3xl md:text-5xl font-bold text-white font-[family-name:var(--font-outfit)] mb-4">
        AI Agent ROI Calculator
      </h1>
      <p className="text-xl text-gray-400 mb-8 leading-relaxed">
        Get an honest estimate based on published industry data. No email required,
        no inflated numbers.
      </p>
      {/* Disclaimer — PROMINENT */}
      <div className="p-4 rounded-lg bg-amber-500/[0.05] border border-amber-500/[0.15] mb-10">
        <p className="text-amber-200/70 text-sm leading-relaxed">
          <strong className="text-amber-300">Important:</strong> These estimates are based on
          published industry averages from IBM, Freshworks, Forrester, and other sources.
          Your actual results will depend on your specific business, industry, implementation
          quality, and customer base. These figures are not guarantees.
        </p>
      </div>
      {/* Calculator Form */}
      <div className="space-y-8 p-6 rounded-xl bg-white/[0.03] border border-white/[0.06]">
        {/* Industry */}
        <div>
          <label htmlFor="roi-industry" className="block text-white font-medium font-[family-name:var(--font-outfit)] mb-2">
            Your Industry
          </label>
          <select
            id="roi-industry"
            aria-label="Select your industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full p-3 rounded-lg bg-white/[0.06] border border-white/[0.1] text-white focus:border-[#4361ee] focus:outline-none transition-colors"
          >
            <option value="" className="bg-[#1a1a2e]">Select your industry...</option>
            {industries.map((ind) => (
              <option key={ind.slug} value={ind.slug} className="bg-[#1a1a2e]">
                {ind.name}
              </option>
            ))}
          </select>
        </div>
        {/* Enquiries per month */}
        <div>
          <label htmlFor="roi-enquiries" className="block text-white font-medium font-[family-name:var(--font-outfit)] mb-2">
            Customer enquiries per month: <span className="text-[#4361ee]">{enquiriesPerMonth}</span>
          </label>
          <input
            id="roi-enquiries"
            aria-label="Customer enquiries per month"
            type="range"
            min={10}
            max={5000}
            step={10}
            value={enquiriesPerMonth}
            onChange={(e) => setEnquiriesPerMonth(Number(e.target.value))}
            className="w-full accent-[#4361ee]"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>10</span><span>5,000</span>
          </div>
        </div>
        {/* Current response time */}
        <div>
          <label htmlFor="roi-response-time" className="block text-white font-medium font-[family-name:var(--font-outfit)] mb-2">
            Average response time currently (hours): <span className="text-[#4361ee]">{currentResponseHours}</span>
          </label>
          <input
            id="roi-response-time"
            aria-label="Average response time in hours"
            type="range"
            min={0.1}
            max={24}
            step={0.1}
            value={currentResponseHours}
            onChange={(e) => setCurrentResponseHours(Number(e.target.value))}
            className="w-full accent-[#4361ee]"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>6 min</span><span>24 hrs</span>
          </div>
        </div>
        {/* Staff count */}
        <div>
          <label htmlFor="roi-staff" className="block text-white font-medium font-[family-name:var(--font-outfit)] mb-2">
            Staff handling customer queries: <span className="text-[#4361ee]">{staffCount}</span>
          </label>
          <input
            id="roi-staff"
            aria-label="Number of staff handling customer queries"
            type="range"
            min={1}
            max={50}
            step={1}
            value={staffCount}
            onChange={(e) => setStaffCount(Number(e.target.value))}
            className="w-full accent-[#4361ee]"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>1</span><span>50</span>
          </div>
        </div>
        {/* Hourly rate */}
        <div>
          <label htmlFor="roi-hourly-rate" className="block text-white font-medium font-[family-name:var(--font-outfit)] mb-2">
            Average hourly staff cost ($): <span className="text-[#4361ee]">${hourlyRate}</span>
          </label>
          <input
            id="roi-hourly-rate"
            aria-label="Average hourly staff cost in pounds"
            type="range"
            min={8}
            max={60}
            step={1}
            value={hourlyRate}
            onChange={(e) => setHourlyRate(Number(e.target.value))}
            className="w-full accent-[#4361ee]"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>$8</span><span>$60</span>
          </div>
        </div>
        <button
          onClick={handleCalculate}
          className="w-full py-4 rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] text-white font-semibold text-lg hover:opacity-90 transition-opacity font-[family-name:var(--font-outfit)]"
        >
          Calculate My Estimated ROI
        </button>
      </div>
      {/* Results */}
      {showResults && (
        <div id="roi-results" className="mt-10 space-y-6">
          <h2 className="text-2xl font-semibold text-white font-[family-name:var(--font-outfit)]">
            Your Estimated Results
          </h2>
          {/* Key metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
              <p className="text-3xl font-bold text-[#22c55e] font-[family-name:var(--font-outfit)]">
                {enquiriesHandledByAI}
              </p>
              <p className="text-gray-400 text-sm mt-1">Enquiries handled by AI/month</p>
              <p className="text-gray-400 text-xs mt-1">Based on 80% (IBM)</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
              <p className="text-3xl font-bold text-[#22c55e] font-[family-name:var(--font-outfit)]">
                {hoursFreedPerMonth}h
              </p>
              <p className="text-gray-400 text-sm mt-1">Staff hours freed/month</p>
              <p className="text-gray-400 text-xs mt-1">At ~8 min per enquiry</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
              <p className="text-3xl font-bold text-[#22c55e] font-[family-name:var(--font-outfit)]">
                ${monthlyCostSaving.toLocaleString()}
              </p>
              <p className="text-gray-400 text-sm mt-1">Estimated monthly saving</p>
              <p className="text-gray-400 text-xs mt-1">Staff time redeployed</p>
            </div>
            <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
              <p className="text-3xl font-bold font-[family-name:var(--font-outfit)]" style={{
                color: roiPercentage > 0 ? '#22c55e' : '#ef4444'
              }}>
                {roiPercentage > 0 ? '+' : ''}{roiPercentage}%
              </p>
              <p className="text-gray-400 text-sm mt-1">Estimated annual ROI</p>
              <p className="text-gray-400 text-xs mt-1">Net savings vs AI cost</p>
            </div>
          </div>
          {/* Before / After */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-5 rounded-xl bg-red-500/[0.05] border border-red-500/[0.15]">
              <h3 className="text-white font-semibold font-[family-name:var(--font-outfit)] mb-3">Current State</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Response time: <span className="text-red-400">{currentResponseHours} hours</span></p>
                <p>Enquiries handled manually: <span className="text-red-400">{enquiriesPerMonth}/month</span></p>
                <p>Monthly staff cost on queries: <span className="text-red-400">${Math.round((enquiriesPerMonth * avgMinutesPerEnquiry / 60) * hourlyRate).toLocaleString()}</span></p>
                <p>After-hours coverage: <span className="text-red-400">None</span></p>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-green-500/[0.05] border border-green-500/[0.15]">
              <h3 className="text-white font-semibold font-[family-name:var(--font-outfit)] mb-3">With AI Agent</h3>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Response time: <span className="text-green-400">Under 1 minute</span></p>
                <p>AI-handled enquiries: <span className="text-green-400">{enquiriesHandledByAI}/month</span></p>
                <p>Estimated monthly saving: <span className="text-green-400">${monthlyCostSaving.toLocaleString()}</span></p>
                <p>After-hours coverage: <span className="text-green-400">24/7</span></p>
              </div>
            </div>
          </div>
          {/* Timeline */}
          <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <h3 className="text-white font-semibold font-[family-name:var(--font-outfit)] mb-3">
              Realistic Timeline
            </h3>
            <div className="space-y-2 text-sm text-gray-400">
              <p>🔵 <strong className="text-gray-300">Week 1-2:</strong> Setup and configuration</p>
              <p>🔵 <strong className="text-gray-300">Month 1-2:</strong> Initial benefits visible (60-90 days per Fullview data)</p>
              <p>🔵 <strong className="text-gray-300">Month 8-14:</strong> Positive ROI typically achieved (industry average)</p>
              <p>🔵 <strong className="text-gray-300">Month 12+:</strong> Full ROI of approx ${ASSUMPTIONS.roiPerPound.toFixed(2)} per $1 invested</p>
            </div>
          </div>
          {/* Disclaimer repeated */}
          <div className="p-4 rounded-lg bg-amber-500/[0.05] border border-amber-500/[0.15]">
            <p className="text-amber-200/70 text-sm leading-relaxed">
              <strong className="text-amber-300">Remember:</strong> These are estimates
              based on published industry averages. Your actual results depend on your
              specific business circumstances. AI achieves 98.2% accuracy on routine
              transactional tasks but only 61.2% on emotionally nuanced conversations
              (AllAboutAI, 12M interactions) — which is why human oversight remains essential.
            </p>
          </div>
          {/* Methodology toggle */}
          <button
            onClick={() => setShowMethodology(!showMethodology)}
            className="text-[#4361ee] hover:underline text-sm"
          >
            {showMethodology ? 'Hide' : 'Show'} calculation methodology and sources ▼
          </button>
          {showMethodology && (
            <div className="p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-4">
              <h3 className="text-white font-semibold font-[family-name:var(--font-outfit)]">
                Calculation Methodology
              </h3>
              <div className="text-sm text-gray-400 space-y-2">
                <p>AI-handled enquiries = Total enquiries × 80% (IBM benchmark)</p>
                <p>Hours freed = AI-handled enquiries × 8 minutes avg ÷ 60</p>
                <p>Monthly saving = Hours freed × Your hourly rate</p>
                <p>Annual ROI = (Annual saving − Annual AI cost) ÷ Annual AI cost × 100</p>
                <p>Estimated AI cost used: ${estimatedMonthlyAICost}/month (mid-tier plan estimate)</p>
              </div>
              <h4 className="text-white font-medium text-sm mt-4">Sources Used</h4>
              <div className="space-y-2">
                {SOURCES.map((s, i) => (
                  <div key={i} className="text-xs text-gray-400">
                    <span className="text-gray-400">{s.claim}</span> —{' '}
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#4361ee] hover:underline">
                      {s.source}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* CTA */}
          <div className="text-center p-8 rounded-xl bg-gradient-to-br from-[#4361ee]/10 to-[#7c3aed]/10 border border-white/[0.06]">
            <h3 className="text-xl font-semibold text-white font-[family-name:var(--font-outfit)] mb-3">
              Want a More Detailed Assessment?
            </h3>
            <p className="text-gray-400 mb-6 text-sm">
              Take our AI Readiness Quiz for a personalised recommendation, or explore
              our guide for your industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/tools/ai-readiness-quiz"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-gradient-to-r from-[#4361ee] to-[#7c3aed] text-white font-medium hover:opacity-90 transition-opacity"
              >
                AI Readiness Quiz
              </Link>
              {industry && (
                <Link
                  href={`/guides/${industry}`}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-white/[0.15] text-white font-medium hover:bg-white/[0.05] transition-colors"
                >
                  Read Your Industry Guide
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
